import _ from 'lodash';
import fp from 'lodash/fp';
import React from 'react';
import { wrapDisplayName } from 'recompose';
import axios from 'axios';
import { withCookies } from 'react-cookie';
import { host } from '@deerplusofficial/pos-helper';
import { Alert } from '@deerplusofficial/pos-components';
import withRouter from './withRouter';

const ErrorAlert = Alert.mixin({ type: 'error' });

const { Provider, Consumer } = React.createContext({
  token: '',
  userId: '',
  posId: '',
  channelId: '',
  mainApi: {},
  POSApi: {},
  COREApi: {},
  onLogin: _.identity,
  onLogout: _.identity,
  onLoginPointOfSale: _.identity,
  onLogoutPointOfSale: _.identity,
  onError: _.identity,
});

export const withClientProvider = () => Component =>
  fp.compose(
    withCookies,
    withRouter()
  )(
    class extends React.Component {
      static displayName = wrapDisplayName(Component, 'WithClientProvider');

      state = {
        token: this.props.cookies.get('token'),
        userId: this.props.cookies.get('userId'),
        posId: this.props.cookies.get('posId'),
        channelId: this.props.cookies.get('channelId'),
      };

      createApi = baseURL => {
        const { token } = this.state;

        return axios.create({
          baseURL,
          headers: { Authorization: token },
        });
      };

      onLogin = async ({ email, password }) => {
        const { cookies, channelId } = this.props;

        try {
          const { accessToken, user_id: userId } = _.get(
            await axios.post(`${host.mainApi}/auth/login`, { email, password }),
            ['data', 'result']
          );
          const token = `Bearer ${accessToken}`;

          cookies.set('token', token, { path: '/', maxAge: 86400 * 30 });
          cookies.set('userId', userId, { path: '/', maxAge: 86400 * 30 });
          cookies.set('channelId', channelId, {
            path: '/',
            maxAge: 86400 * 30,
          });

          this.setState({ token, userId, channelId });
        } catch (e) {
          if (
            e.response &&
            e.response.status === 400 &&
            e.response.data.code === 103
          )
            return { status: 'error' };

          this.onError(e);
        }
      };

      onLogout = () => {
        const { cookies, pathTo } = this.props;
        let token, userId, posId, channelId;

        if (this.state.token) {
          cookies.remove('userId', { path: '/' });
          cookies.remove('token', { path: '/' });
          cookies.remove('posId', { path: '/' });
          cookies.remove('channelId', { path: '/' });

          this.setState({ token, userId, posId, channelId });
        }

        window.location = pathTo.login;
      };

      onLoginPointOfSale = async posId => {
        const { channelId, cookies } = this.props;

        try {
          await this.POSApi.post(
            `/channels/${channelId}/point_of_sales/${posId}/user`,
            {}
          );

          cookies.set('posId', posId, { path: '/', maxAge: 86400 * 30 });

          this.setState({ posId });
        } catch (e) {
          this.onError(e);
        }
      };

      onLogoutPointOfSale = async posId => {
        const { channelId, cookies } = this.props;

        try {
          await this.POSApi.delete(
            `/channels/${channelId}/point_of_sales/${posId}/user`
          );
          cookies.remove('posId', { path: '/' });

          this.setState({ posId: '' });
        } catch (e) {
          this.onError(e);
        }
      };

      onError = e => {
        if (axios.isCancel(e)) return;

        const { history } = this.props;
        const status = _.get(e, ['response', 'status']);
        const { code, result, message } = _.get(e, ['response', 'data'], {});

        if (status === 500) return ErrorAlert('伺服器異常，請聯繫開發人員');

        if (status === 401) return this.onLogout();

        if (status === 400 && code === 115) {
          const items = _.map(result.product_items, ({ name }) => name);

          return ErrorAlert(_.join([...items, '庫存不足 Out of stock'], '\n'));
        }

        if (status === 400 && _.size(message))
          return ErrorAlert(`Bad Request: ${message}`);

        if (status === 404) return history.replace('/');

        if (e.message || _.size(message))
          return ErrorAlert(`Error: ${e.message || message}`);

        return ErrorAlert(
          'Oops!\n目前網路中斷請確認您的網路狀態並重新嘗試連線。'
        );
      };

      get mainApi() {
        return this.createApi(host.mainApi);
      }

      get POSApi() {
        return this.createApi(host.POSApi);
      }

      get COREApi() {
        return this.createApi(host.COREApi);
      }

      get value() {
        const { token, userId, posId, channelId } = this.state;

        return {
          token,
          userId,
          posId,
          channelId,
          mainApi: this.mainApi,
          POSApi: this.POSApi,
          COREApi: this.COREApi,
          onLogin: this.onLogin,
          onLogout: this.onLogout,
          onLoginPointOfSale: this.onLoginPointOfSale,
          onLogoutPointOfSale: this.onLogoutPointOfSale,
          onError: this.onError,
        };
      }

      render() {
        return (
          <Provider value={this.value}>
            <Component {...this.props} />
          </Provider>
        );
      }
    }
  );

export default () => Component =>
  class extends React.Component {
    static displayName = wrapDisplayName(Component, 'WithClient');

    render() {
      return (
        <Consumer>
          {client => <Component client={client} {...this.props} />}
        </Consumer>
      );
    }
  };



// WEBPACK FOOTER //
// ../pos-master/packages/pos-hoc/src/withClient.js